/* eslint-disable react-hooks/exhaustive-deps */
import { zodResolver } from '@hookform/resolvers/zod'
import { json, type ActionFunctionArgs } from '@remix-run/node'
import { useActionData } from '@remix-run/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'
import {
	SET_INITIAL_QUESTION_TREE,
	SET_LANGUAGE,
} from '#app/constants/forms.ts'
import { onSubmit } from '#app/features/chat/components/chat/conversation/utils/onSubmit.ts'
import { WizardFormContainer } from '#app/features/form/components/wizardFormContainer.tsx'
import {
	initialSessionTree,
	initialStateSession,
} from '#app/features/form/forms/collectionForms/session/initialState.ts'
import { SessionForm } from '#app/features/form/forms/collectionForms/session/sessionForm.tsx'
import { getValidatedFormData } from '#app/features/form/forms/utils/getValidatedFormData.ts'
import { globalJSONStore } from '#app/features/form/wizard/reducer/createGlobalFormStore.ts'

import { useCurrentLanguage } from '#app/hooks/use-current-language.tsx'
import {
	gptFunctionIntakeQuestions,
	QuestionExampleTemplate,
} from '#app/pages/chat/backend/openaiPrompts.ts'
import { requireUserId } from '#app/utils/auth.server.ts'
import { mixpanel } from '#app/utils/mixpanel.server.ts'
import { rateCreateOrPassDynamicField } from '#server/form/dynamicForms/rateCreateOrPassDynamicField.ts'
import { serverTranslations } from '#server/utils/serverTranslations.ts'
import { useGlobalChatStore } from '../chat+/index.tsx'

interface FormState {
	[key: string]: string
}

interface SystemPrompt {
	role: 'system'
	content: string
}

interface WizardMessage {
	backendId: string
	content: Array<{ question: string; answer: string }>
}

interface FormattedMessage {
	role: 'assistant' | 'user' | 'system'
	content: string
}

function formatWizardMessages(
	wizardMessages: WizardMessage[],
): FormattedMessage[] {
	const formattedMessages: FormattedMessage[] = []

	wizardMessages.forEach(wizardMessage => {
		wizardMessage.content.forEach(({ question, answer }) => {
			// Add question as an assistant message
			formattedMessages.push({
				role: 'assistant',
				content: question,
			})

			// Add answer as a user message
			formattedMessages.push({
				role: 'user',
				content: answer,
			})
		})
	})

	return formattedMessages
}

function createSystemPrompt(formState: FormState): SystemPrompt {
	let challengeType: 'personal challenges' | 'interpersonal challenges' =
		'personal challenges'
	let contentUser: string = ''

	Object.entries(formState).forEach(([key, value]) => {
		if (key.startsWith('personalChallenges')) {
			challengeType = 'personal challenges'
		} else if (key.startsWith('interpersonalChallenges')) {
			challengeType = 'interpersonal challenges'
		}

		if (value && value.includes('question') && value.includes('answer')) {
			const { question, answer } = parseQuestionAnswer(value)
			contentUser += `question=${question}\nanswer=${answer}\n\n`
		}
	})

	const prompt: SystemPrompt = {
		role: 'system',
		content: `The user has filled in a wizard form and wants to focus on ${challengeType}. Here are the questions and answers that they gave:\n\n${contentUser}`,
	}

	return prompt
}

function parseQuestionAnswer(value: string): {
	question: string
	answer: string
} {
	const parts = value
		.split('&')
		.reduce((acc: { [key: string]: string }, part) => {
			const [key, val] = part.split('=')
			acc[key] = decodeURIComponent(val)
			return acc
		}, {})

	return { question: parts.question, answer: parts.answer }
}

const zodAnySchema = z.any()
const resolver = zodResolver(zodAnySchema)

export const action = async ({ request }: ActionFunctionArgs) => {
	const userId = await requireUserId(request)
	const { data } = await getValidatedFormData<any>(request, resolver)
	const t = await serverTranslations(request, ['introductionGPTQuestions'])

	if (data.id.includes('question')) {
		const newDynamicField = await rateCreateOrPassDynamicField({
			data,
			tools: gptFunctionIntakeQuestions(t),
			perfectExampleTemplate: QuestionExampleTemplate(t),
		})

		const currentScore = newDynamicField.serverData?.currentScores?.find(
			score => score.id === data.id,
		)

		if (currentScore && currentScore.score >= 4) {
			const currentFormstate = newDynamicField?.serverData?.formState
			const currentMessages = newDynamicField?.serverData?.wizardMessages

			const systemPromptForAI = createSystemPrompt(
				currentFormstate as FormState,
			)

			const messagesForChat = formatWizardMessages(currentMessages as any)

			let messages: FormattedMessage[] = []

			if (systemPromptForAI) {
				messages.push(systemPromptForAI as FormattedMessage)
			}

			if (messagesForChat) {
				messages = [...messages, ...messagesForChat]
			}
			// Create conversation and add:
			// System message with context about what the user chose in options
			// Add all the latests answers and if the rating is high enough, to use the last answer
			// to kickstart a new conversation

			mixpanel.track('Started Intake', {
				distinct_id: userId,
				timeStamp: new Date().toISOString(),
			})

			return json({
				serverData: newDynamicField.serverData,
				error: newDynamicField.error,
				type: 'set_new_chat_message',
				messages,
				id: 'question',
			})
		}

		return json({
			serverData: newDynamicField.serverData,
			error: newDynamicField.error,
			type: 'reset_state_with_new_data',
			id: 'question',
		})
	}

	return json({
		data,
	})
}

export default function Session() {
	return (
		<>
			<SessionFormGlobal />
		</>
	)
}

export const SessionFormGlobal = ({ messageFetcher, textareaRef }: any) => {
	const actionData = useActionData<typeof action>()

	const { t } = useTranslation(['intakeTrees'])
	const dispatchChat = useGlobalChatStore(useShallow(state => state.dispatch))
	const newConversationId = uuidv4()
	const showEmptyChat = useGlobalChatStore(state => state.showEmptyChat)
	const typeOfView = useGlobalChatStore(state => state.typeOfView)

	const dispatch = globalJSONStore(useShallow(state => state.dispatch))
	const currentLanguage = useCurrentLanguage()
	const languageId = globalJSONStore(state => state.languageId)
	const formId = globalJSONStore(state => state.formId)
	const setMessageSignal = globalJSONStore(state => state.setMessage)
	const messagesForChat = globalJSONStore(state => state.messageContext)

	const trees = initialSessionTree(t)

	useEffect(() => {
		if (formId === '' || formId !== 'session') {
			dispatch({
				type: 'SET_ENTIRE_STATE',
				payload: initialStateSession as any,
			})
		}
	}, [dispatch, formId])

	useEffect(() => {
		if (currentLanguage !== languageId) {
			dispatch({ type: SET_LANGUAGE, languageId: currentLanguage })
			dispatch({ type: SET_INITIAL_QUESTION_TREE, questionTree: trees })
		}
	}, [currentLanguage, dispatch, languageId, trees])

	useEffect(() => {
		if (setMessageSignal) {
			const interactionMessages = messagesForChat.filter(
				(message: any) =>
					message.role === 'user' || message.role === 'assistant',
			)

			const systemMessages = messagesForChat.filter(
				(message: any) => message.role === 'system',
			)

			onSubmit({
				messages: {
					interaction: interactionMessages,
					system: systemMessages,
				},
				dispatch: dispatchChat,
				messageFetcher,
				showEmptyChat,
				conversationId: newConversationId,
				textareaRef,
				typeOfView,
			})
		}
	}, [setMessageSignal, messagesForChat])

	return (
		<WizardFormContainer isPage>
			<SessionForm data={actionData} />
		</WizardFormContainer>
	)
}
